import { InjectionToken } from '@angular/core';
import { ActivatedRouteSnapshot, ParamMap } from '@angular/router';
import { LocaleStartParams } from '@platform-frontend/transloco';
import { Observable } from 'rxjs';
import { AccountCustomizedTexts } from './account-customized-texts';
import { AccountStartParams } from './account-router';
import { AuthenticationContext } from './auth-context';
import { AccountStartupContext } from './model/account-startup-context';
import { OAuthProvider } from './o-auth';
import { IOAuthPopupService } from './o-auth/o-auth-popup-service';

export interface IAuthenticationService {
  /**
   * Start params to be encoded inside the authentication routes.
   */
  getAppStartParams(queryParamMap: ParamMap): LocaleStartParams | undefined;

  /**
   * Actions to be done during startup of account modules.
   */
  getStartupObservable(startParams: AccountStartParams, context: AccountStartupContext): Observable<unknown>;

  /**
   * TODO (MP-2518): Remove the adjustRedirectUrl workaround.
   */
  adjustRedirectUrl(url: string): string | undefined;

  /**
   * If no replaceUrl route parameter is passed, navigations are done with replaceUrl set to true if this method returns true and without replaceUrl parameter otherwise.
   */
  getGotoActionReplaceUrlDefault(): Observable<boolean>;

  /**
   * Returns true if the application is currently visible, false otherwise.
   */
  isApplicationVisible(): Observable<boolean>;

  /**
   * Returns true if the application can close itself.
   */
  closeApplicationOnSignOut(): boolean;

  /**
   * All components of the authentication module are direct parents of the returned route.
   */
  getRouteParent(): string;

  /**
   * Performs actions that should be taken if no redirection is set.
   */
  finishAuthenticationWithoutRedirection(context: AuthenticationContext, clearNotifications?: false): Observable<unknown>;

  /**
   * Shows a notification on successful sign up if needed.
   * @param redirectUrl The redirect url that could be used to chose the corresponding notification.
   */
  adjustConfirmSignUpSuccessNotification(redirectUrl: string): Observable<unknown>;

  /**
   * Performs actions that should be taken if no redirection is set.
   */
  finishConfirmAuthenticationEmailWithoutRedirection(context: AuthenticationContext): Observable<unknown>;

  /**
   * Performs actions that should be taken if no redirection is set.
   */
  finishResetPasswordWithoutRedirection(): Observable<unknown>;

  /**
   * Performs actions that should be taken if no redirection is set.
   */
  finishSetPasswordWithoutRedirection(): Observable<unknown>;

  /**
   * Get an implementation for handling oAuth popups.
   */
  getOAuthPopupService(): IOAuthPopupService;

  /**
   * Get account customized texts.
   */
  getAccountCustomizedTexts(): AccountCustomizedTexts;

  /**
   * Get ordered oAuth providers
   */
  getOrderedOAuthProviders(): Observable<OAuthProvider[]>;

  /**
   * Get success lottie path
   */
  getSuccessLottiePath(): string | null;

  /**
   * Set header field for route data
   */
  setHeaderDataField(route: ActivatedRouteSnapshot): string | null;
}

export interface IAccountDisabledService {
  /**
   * All components of the account-disabled module are direct parents of the returned route.
   */
  getRouteParent(): string;

  /**
   * If true the navigation to the deactivated account components is enabled, false otherwise.
   */
  gotoDeactivatedAccountRoutes(): boolean;

  /**
   * Performs actions that should be taken if no redirection is set.
   */
  finishAccountReactivationWithoutRedirection(): Observable<unknown>;

  /**
   * Performs actions that should be taken if no redirection is set.
   */
  finishTermsDeclineWithoutRedirection(): Observable<unknown>;

  /**
   * Performs actions that should be taken if no redirection is set.
   */
  finishTermsAcceptanceWithoutRedirection(): Observable<unknown>;

  /**
   * Defines, if the disabled acccount component should have a cancel button.
   */
  showCancelButtonsOnDeactivatedAccount(): boolean;
}

export const AuthenticationServiceToken = new InjectionToken<IAuthenticationService>('AuthenticationService');
export const AccountDisabledServiceToken = new InjectionToken<IAccountDisabledService>('AccountDisabledService');
